import React from 'react';
import { motion, useMotionValue, useTransform, useSpring, useReducedMotion } from 'motion/react';

interface MouseParallaxProps {
  children: React.ReactNode;
  className?: string;
  strength?: number;
}

export default function MouseParallax({
  children,
  className = '',
  strength = 20,
}: MouseParallaxProps) {
  const prefersReducedMotion = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const springConfig = { stiffness: 150, damping: 20 };
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [strength / 2, -strength / 2]), springConfig);
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-strength / 2, strength / 2]), springConfig);

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  function handleMouseMove(event: React.MouseEvent<HTMLDivElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set((event.clientX - centerX) / rect.width);
    y.set((event.clientY - centerY) / rect.height);
  }

  function handleMouseLeave() {
    x.set(0);
    y.set(0);
  }

  return (
    <motion.div
      className={className}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateX,
        rotateY,
        transformPerspective: 1000,
      }}
    >
      {children}
    </motion.div>
  );
}
